/**
 * ゆめさくコード Design Tokens - Colors
 *
 * Color scales and semantic color tokens for the design system.
 */

// Primary Color Scale
export const primary = {
  50: '#E6F8E7',
  75: '#CDEFD2',
  100: '#A7E5B8',
  200: '#7ED89D',
  300: '#4FC67F',
  400: '#05B45B', // Main brand color
  500: '#049A4E',
  600: '#037F40',
  700: '#026332',
  800: '#014826',
  900: '#012E1A',
  950: '#012E1A',
} as const;

// Black / Gray Scale
export const black = {
  0: '#FFFFFF',
  25: '#FDFDFA',
  50: '#FAFAEF',
  75: '#F2F2E6',
  100: '#DCDCD9',
  200: '#BDBDBA',
  300: '#A2A29F',
  400: '#898987',
  500: '#727270',
  600: '#5D5D5B',
  700: '#494947',
  800: '#363635',
  850: '#353534',
  875: '#2A2A28', // 暗い版の乗せたとき (code-server の暗い版の list.hoverBackground)
  900: '#20201F',
  925: '#1A1A19', // 暗い版の帯と脇の面 (code-server の暗い版の statusBar.background)
  950: '#0A0A0A',
} as const;

// Accent Color Scale
export const accent = {
  50: '#FEEDEC',
  100: '#FCD2CE',
  200: '#FAA79E',
  300: '#F97C6A',
  400: '#F15025', // Main accent color
  500: '#D64620',
  600: '#B33919',
  700: '#8C2B11',
  800: '#631C09',
  900: '#3B0D03',
  950: '#260601',
} as const;

// HP の次の行動の黄 (便箋のボタンと印と同じ値)。キーボードの focus の輪にも使う。
// 明るい地との比は 1.2 しかないので、輪にするときは text-main の 1px を内側に重ねる
export const focus = {
  ring: '#FFE566',
} as const;

// Beige Color Scale
export const beige = {
  50: '#F4F0EA',
  100: '#E6DBC9',
  200: '#CABCA4',
  300: '#ADA18C',
  400: '#938977',
  500: '#7B7263',
  600: '#6A6255',
  700: '#524C42',
  800: '#39352D',
  900: '#201D18',
  950: '#13110D',
} as const;

// Surface Colors (Light Mode)
export const surfaceLight = {
  primary: primary[400],
  primaryLight: primary[200],
  gray: black[500],
  glayLight: black[300],
  disabled: black[400],
  accent: accent[400],
  error: '#FFFFFF',
} as const;

// Surface Colors (Dark Mode)
export const surfaceDark = {
  primary: primary[600], // 白い字を乗せる塗り。primary[300] だと白い字が 2.2 しか無い
  primaryLight: primary[500],
  gray: black[700],
  glayLight: black[500],
  disabled: black[800],
  accent: accent[400],
  error: '#F47365',
} as const;

// Text Colors (Light Mode)
export const textLight = {
  main: black[800],
  sub: black[500],
  placeholder: black[300],
  white: black[0],
  primary: primary[600],
  disabled: black[400], // Surface/Disabled
  accent: accent[600],
} as const;

// Text Colors (Dark Mode)
export const textDark = {
  main: black[0],
  sub: black[200],
  placeholder: black[300],
  white: black[0],
  primary: primary[300],
  primaryDark: primary[200], // 名前は濃いだが、暗い地の上では明るい側
  disabled: black[800],
  accent: accent[400],
} as const;

// Background Colors (Light Mode)
export const backgroundLight = {
  main: black[25],
  second: black[50],
  third: black[75],
  primaryLight: primary[50],
  primaryDark: primary[200],
  accentLight: accent[50],
  accentDark: accent[900],
} as const;

// Background Colors (Dark Mode)
// 地は緑にしない。クラウドエディターの暗い版と同じ、わずかに暖かい灰色の段 (app.css の :root[data-theme="dark"] と同じ値)
export const backgroundDark = {
  main: black[925],
  surface: black[900],
  second: black[875],
  third: black[850],
  primaryLight: '#1E3A2B', // 選ばれた札の地。暗い地に緑を 2 割ほど混ぜた色
  primaryDark: primary[700],
  accentLight: accent[50],
  accentDark: accent[900],
} as const;

// Export all colors
export const colors = {
  primary,
  black,
  accent,
  beige,
  focus,
  surface: {
    light: surfaceLight,
    dark: surfaceDark,
  },
  text: {
    light: textLight,
    dark: textDark,
  },
  background: {
    light: backgroundLight,
    dark: backgroundDark,
  },
} as const;

// Type exports
export type PrimaryScale = typeof primary;
export type BlackScale = typeof black;
export type AccentScale = typeof accent;
export type BeigeScale = typeof beige;
export type SurfaceColors = typeof surfaceLight;
export type TextColors = typeof textLight;
export type BackgroundColors = typeof backgroundLight;
